import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { resolveWorkspaceViewerReturnUrl } from "@/lib/navigation/documentViewerReturn";
import { useDocumentViewerBreadcrumbs } from "@/hooks/useDocumentViewerBreadcrumbs";
import { useDocumentViewerStackSync } from "@/hooks/useDocumentViewerContextSync";
import Workspace, {
  WorkspaceDocument,
  WorkspaceDocumentType,
} from "../../models/workspace/Workspace";
import AppRoutes from "../../constants/routes/AppRoutes";
import { useServices } from "../../hooks/useServices";
import useWorkspace from "../../hooks/useWorkspace";
import { useDocumentReferenceStore } from "../../stores/document-reference.store";
import { useWorkspaceDocumentVersionStore } from "../../stores/workspace-document-version.store";
import { DocumentCategory } from "@/models/legislation/Legislation";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { DocumentPDFHeader } from "../chat/components/LegislationPDFHeader";
import WorkspaceDocumentVersionIndicator from "./components/WorkspaceDocumentVersionIndicator";
import DocumentSidebar from "@/components/content-sidebar/document-sidebar/document-sidebar";
import { DocumentTab } from "@/components/content-sidebar/document-sidebar/document-tab-triggers";
import PageContent from "../PageContent";
import EmbedPDFViewer from "@/partials/pdf-viewer/EmbedPDFViewer";
import useTenant from "@/hooks/useTenant";
import { Alert, AlertDescription } from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { formatDateToDanishForFilename } from "@/utils/format";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import { isWorkspaceDocumentPreviewableInViewer } from "@/pages/workspace/utils/workspaceItemUtils";
import type { PreviewPdfSheetItem } from "@/services/workspaceServices";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import { DataSource } from "@/config/datasources";

const WorkspaceDocumentView = () => {
  /**** Hooks ****/
  const navigate = useNavigate();
  const { blobName, workspaceId, type } = useParams();
  const { useQuerySingle } = useWorkspace();
  const { useQueryGetParentTenant } = useTenant();
  const { selectedDocumentReference, selectedBoundingBoxes } =
    useDocumentReferenceStore();
  const {
    selectedVersion,
    setSelectedVersion,
    setWorkspaceDocument,
    workspaceDocument,
    setWorkspace: setWorkspaceInStore,
  } = useWorkspaceDocumentVersionStore();
  const workspaceService = useServices().items.workspace;

  /**** State ****/
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string>("");
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const previewPdfBlobUrlRef = useRef<string | null>(null);
  const [previewSheets, setPreviewSheets] = useState<
    PreviewPdfSheetItem[] | null
  >(null);
  const [selectedSheetIndex, setSelectedSheetIndex] = useState<number>(0);
  const sheetBlobUrlsRef = useRef<Record<number, string>>({});

  /**** Queries ****/
  const fetchedWorkspace = useQuerySingle(workspaceId);
  const parentTenantQuery = useQueryGetParentTenant(
    workspace?.isParentWorkspace,
    {
      queryKey: ["parentTenant", workspace?.isParentWorkspace],
    }
  );

  /**** Sidebar ****/
  useEffect(() => {
    if (!blobName) {
      navigate(AppRoutes.workspaceFileNotFound, {
        replace: true,
      });
      return;
    }
    if (!workspaceId) {
      navigate(AppRoutes.workspaceNotFound, {
        replace: true,
      });
      return;
    }
  }, [blobName, workspaceId, navigate]);

  useEffect(() => {
    try {
      if (fetchedWorkspace?.isFetched && fetchedWorkspace?.data) {
        setWorkspace(fetchedWorkspace.data);
        setWorkspaceInStore(fetchedWorkspace.data);
        const documentMatchesRoute = (document: WorkspaceDocument): boolean =>
          document.blobName === blobName ||
          document.id === blobName ||
          document.revisions.some((revision) => revision.blobId === blobName);

        const getMatchingRevision = (document: WorkspaceDocument) =>
          document.revisions.find((revision) => revision.blobId === blobName);

        const findDocumentInFolders = (
          folders: Workspace["folders"]
        ): {
          document: WorkspaceDocument;
          matchingRevision?: WorkspaceDocument["revisions"][number];
        } | null => {
          for (const folder of folders) {
            const foundDoc = folder.documents.find(documentMatchesRoute);
            if (foundDoc) {
              return {
                document: foundDoc,
                matchingRevision: getMatchingRevision(foundDoc),
              };
            }
            const nestedDoc = findDocumentInFolders(folder.folders);
            if (nestedDoc) return nestedDoc;
          }
          return null;
        };
        const topLevelDocument =
          fetchedWorkspace.data.documents.find(documentMatchesRoute);
        const nestedDocument = findDocumentInFolders(fetchedWorkspace.data.folders);
        const document =
          topLevelDocument ?? nestedDocument?.document ?? workspaceDocument;
        const matchingRevision =
          (topLevelDocument && getMatchingRevision(topLevelDocument)) ??
          nestedDocument?.matchingRevision;

        if (!document) {
          navigate(AppRoutes.chatFileNotFound, {
            replace: true,
          });
          return;
        }
        setWorkspaceDocument({
          ...document,
          workspaceId: workspaceId,
        });
        const activeRevision = document.revisions?.find(
          (revision) => revision.revisionVersion === document.activeRevision
        );

        const fallbackUpdatedAt = (() => {
          if (!document.updatedAt) return new Date();
          const parsed = new Date(document.updatedAt);
          return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
        })();

        setSelectedVersion({
          blobName: matchingRevision?.blobId ?? activeRevision?.blobId ?? document.blobName,
          name: document.name,
          updatedAt:
            matchingRevision?.updatedAt ?? activeRevision?.updatedAt ?? fallbackUpdatedAt,
          revisionVersion:
            matchingRevision?.revisionVersion ??
            activeRevision?.revisionVersion ??
            document.activeRevision,
          size: matchingRevision?.size ?? activeRevision?.size ?? document.size ?? 0,
          documentType: document.documentType,
        });
      }
    } catch (error) {
      logger.error("Failed to resolve workspace document from the route", {
        operation: "resolveWorkspaceDocument",
        error,
        workspaceId,
        blobId: blobName,
      });
      navigate(AppRoutes.chatFileNotFound, {
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchedWorkspace?.data,
    fetchedWorkspace?.isFetched,
    blobName,
    navigate,
    setWorkspaceDocument,
    setSelectedVersion,
  ]);

  useEffect(() => {
    const revokeAllSheetBlobUrls = () => {
      Object.values(sheetBlobUrlsRef.current).forEach((url) =>
        window.URL.revokeObjectURL(url)
      );
      sheetBlobUrlsRef.current = {};
    };

    const getViewUrl = async () => {
      try {
        if (!workspaceId || !workspaceDocument) return;

        const currentBlobName = selectedVersion
          ? selectedVersion.blobName
          : blobName;

        if (!currentBlobName) return;

        let revisionVersion: number;
        if (selectedVersion) {
          revisionVersion = selectedVersion.revisionVersion;
        } else {
          revisionVersion = workspaceDocument.activeRevision;
        }

        const parentTenantId = workspace?.isParentWorkspace
          ? parentTenantQuery.data?.id
          : undefined;

        const isXlsxOrXls =
          type?.toLowerCase() === "xlsx" ||
          type?.toLowerCase() === "xls" ||
          type?.toLowerCase() === "xlsm";

        if (isXlsxOrXls) {
          const sheetsResponse =
            await workspaceService.getDocumentPreviewPdfSheets(
              workspaceId,
              workspaceDocument.id,
              currentBlobName,
              revisionVersion,
              parentTenantId
            );

          if (
            sheetsResponse?.success &&
            sheetsResponse.value.sheets?.length > 0
          ) {
            setPreviewSheets(sheetsResponse.value.sheets);
            setSelectedSheetIndex(0);
            setDownloadUrl("");
            return;
          }
        }

        setPreviewSheets(null);

        // Prefer full preview-pdf (on-the-fly conversion, preserves formatting)
        const previewResponse =
          await workspaceService.getDocumentPreviewPdfBlob(
            workspaceId,
            workspaceDocument.id,
            currentBlobName,
            revisionVersion,
            parentTenantId
          );

        if (previewResponse?.success && previewResponse.value.size > 0) {
          if (previewPdfBlobUrlRef.current) {
            window.URL.revokeObjectURL(previewPdfBlobUrlRef.current);
            previewPdfBlobUrlRef.current = null;
          }
          const blobUrl = window.URL.createObjectURL(previewResponse.value);
          previewPdfBlobUrlRef.current = blobUrl;
          setDownloadUrl(blobUrl);
          return;
        }

        // Fallback: view-url (e.g. original blob or existing conversion)
        let isIndexed = false;
        if (selectedVersion) {
          const revision = workspaceDocument.revisions?.find(
            (r) => r.revisionVersion === selectedVersion.revisionVersion
          );
          isIndexed = revision?.isIndexed ?? false;
        } else {
          const activeRevision =
            workspaceDocument.revisions?.[workspaceDocument.activeRevision - 1];
          isIndexed = activeRevision?.isIndexed ?? false;
        }

        const response = await workspaceService.getDocumentViewUrl(
          workspaceId,
          workspaceDocument.id,
          currentBlobName,
          revisionVersion,
          isIndexed,
          parentTenantId
        );

        if (!response?.success) {
          logger.error("Failed to get a view URL for the workspace document", {
            operation: "loadWorkspaceDocumentPreview",
            workspaceId,
            documentId: workspaceDocument.id,
            revisionVersion: selectedVersion?.revisionVersion,
            status: response?.status,
          });
          showErrorToast(
            "Der opstod en fejl ved hentning af adgang til dokumentet. Prøv at opdatere siden eller kontakt support."
          );
          return;
        }

        setDownloadUrl(response.value.url);
      } catch (error) {
        logger.error("Failed to load workspace document preview", {
          operation: "loadWorkspaceDocumentPreview",
          error,
          workspaceId,
          documentId: workspaceDocument?.id,
          revisionVersion: selectedVersion?.revisionVersion,
        });
        showErrorToast(
          "Der opstod en uventet fejl ved hentning af dokument URL. Prøv at opdatere siden eller kontakt support."
        );
      }
    };

    // Guard: Only run when everything is ready
    if (!blobName || !workspace || !workspaceDocument || !workspaceId) return;

    if (workspace.isParentWorkspace) {
      if (parentTenantQuery.isFetched && parentTenantQuery.data?.id) {
        getViewUrl();
      }
    } else {
      getViewUrl();
    }

    return () => {
      if (previewPdfBlobUrlRef.current) {
        window.URL.revokeObjectURL(previewPdfBlobUrlRef.current);
        previewPdfBlobUrlRef.current = null;
      }
      revokeAllSheetBlobUrls();
    };
  }, [
    blobName,
    workspace,
    workspaceDocument,
    workspaceId,
    type,
    parentTenantQuery.data?.id,
    parentTenantQuery.isFetched,
    selectedVersion,
    workspaceService,
    selectedDocumentReference,
  ]);

  // Load PDF for the selected sheet when in sheet mode (XLSX)
  useEffect(() => {
    if (
      !previewSheets?.length ||
      !workspaceId ||
      !workspaceDocument ||
      selectedSheetIndex == null
    )
      return;

    const currentBlobName = selectedVersion?.blobName ?? blobName;
    if (!currentBlobName) return;

    const revisionVersion = selectedVersion?.revisionVersion ?? workspaceDocument.activeRevision;
    const parentTenantId = workspace?.isParentWorkspace
      ? parentTenantQuery.data?.id
      : undefined;

    const cached = sheetBlobUrlsRef.current[selectedSheetIndex];
    if (cached) {
      setDownloadUrl(cached);
      return;
    }

    // No cache: show loading state instead of previous sheet
    setDownloadUrl("");

    let cancelled = false;
    workspaceService
      .getDocumentPreviewPdfSheetBlob(
        workspaceId,
        workspaceDocument.id,
        currentBlobName,
        revisionVersion,
        selectedSheetIndex,
        parentTenantId
      )
      .then((res) => {
        if (cancelled || !res?.success || res.value.size === 0) return;
        const blobUrl = window.URL.createObjectURL(res.value);
        sheetBlobUrlsRef.current[selectedSheetIndex] = blobUrl;
        setDownloadUrl(blobUrl);
      })
      .catch((error) => {
        if (!cancelled) {
          logger.error("Failed to load a spreadsheet preview sheet", {
            operation: "loadPreviewPdfSheet",
            error,
            workspaceId,
            documentId: workspaceDocument.id,
            revisionVersion,
            sheetIndex: selectedSheetIndex,
          });
          showErrorToast(
            "Kunne ikke hente ark. Prøv at opdatere siden."
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    previewSheets,
    selectedSheetIndex,
    workspaceId,
    workspace,
    workspaceDocument,
    blobName,
    selectedVersion?.blobName,
    selectedVersion?.revisionVersion,
    parentTenantQuery.data?.id,
    workspaceService,
  ]);

  const generateFilenameWithTimestamp = (
    item: WorkspaceDocument,
    version?: number
  ): string => {
    if (!item.updatedAt) {
      return item.name;
    }

    const updatedDate = new Date(
      item.revisions?.find((revision) => revision.revisionVersion === version)
        ?.updatedAt || item.updatedAt
    );
    const formattedDate = formatDateToDanishForFilename(updatedDate);

    const lastDotIndex = item.name.lastIndexOf(".");
    if (lastDotIndex === -1) {
      return `${item.name}_${formattedDate}`;
    } else {
      const nameWithoutExt = item.name.substring(0, lastDotIndex);
      const extension = item.name.substring(lastDotIndex);
      return `${nameWithoutExt}_${formattedDate}${extension}`;
    }
  };

  const download = async () => {
    if (!workspaceDocument || !selectedVersion || !workspaceId) {
      return;
    }
    setIsDownloading(true);

    try {
      // Determine blob ID from the selected revision
      const blobId = workspaceDocument.activeRevision
        ? workspaceDocument.revisions?.find(
            (revision) =>
              revision.revisionVersion === selectedVersion.revisionVersion
          )?.blobId
        : workspaceDocument.blobName;

      if (!blobId) {
        logger.error("Workspace document revision has no blob id", {
          operation: "downloadWorkspaceDocument",
          workspaceId,
          documentId: workspaceDocument.id,
          revisionVersion: selectedVersion.revisionVersion,
        });
        showErrorToast("Kunne ikke finde dokumentet");
        setIsDownloading(false);
        return;
      }

      // Always use isIndexed=false for downloads to fetch the original file
      // from the 'documents' container. The 'workspace-documents' container
      // holds PDF-converted versions intended for the viewer, not for download.
      const urlResponse = await workspaceService.getDocumentDownloadUrl(
        workspaceId,
        workspaceDocument.id,
        blobId,
        selectedVersion.revisionVersion,
        false,
        workspace?.isParentWorkspace ? parentTenantQuery.data?.id : undefined
      );

      if (!urlResponse.success) {
        logger.error(
          "Failed to get a download URL for the workspace document",
          {
            operation: "downloadWorkspaceDocument",
            workspaceId,
            documentId: workspaceDocument.id,
            revisionVersion: selectedVersion.revisionVersion,
            status: urlResponse.status,
          }
        );
        showErrorToast("Fejl ved hentning af download URL");
        setIsDownloading(false);
        return;
      }

      const response = await fetch(urlResponse.value.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = generateFilenameWithTimestamp(
        workspaceDocument,
        selectedVersion.revisionVersion
      );
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error("Failed to download workspace document", {
        operation: "downloadWorkspaceDocument",
        error,
        workspaceId,
        documentId: workspaceDocument.id,
        revisionVersion: selectedVersion.revisionVersion,
      });
      showErrorToast("Der opstod en fejl under download");
    } finally {
      setIsDownloading(false);
    }
  };

  const cameFromChat = window.location.pathname.includes("/chat/");

  const displayDocument: WorkspaceDocument | undefined =
    selectedVersion && workspaceDocument
      ? {
          ...workspaceDocument,
          blobName: selectedVersion.blobName,
          size: selectedVersion.size,
          name: selectedVersion.name,
        }
      : workspaceDocument || undefined;

  const fallbackTitle =
    displayDocument?.name || workspaceDocument?.name || "Dokumentvisning";

  const resolveBaseReturnUrl = useCallback(() => {
    if (!workspaceId) {
      return AppRoutes.workspaces;
    }
    return resolveWorkspaceViewerReturnUrl({
      workspaceId,
      workspace,
      documentId: workspaceDocument?.id,
      cameFromChat,
    });
  }, [workspaceId, workspace, workspaceDocument?.id, cameFromChat]);

  const { parentBreadcrumbs, flatParentBreadcrumbs, currentTitle, currentFullTitle, handleViewerBack } =
    useDocumentViewerBreadcrumbs({
      fallbackTitle,
      resolveBaseReturnUrl,
    });

  useDocumentViewerStackSync({
    title: fallbackTitle,
    rootLabel:
      !cameFromChat && workspace?.name
        ? `Workspace: ${workspace.name}`
        : undefined,
    workspaceId: cameFromChat ? undefined : workspaceId,
  });

  return (
    <>
      {workspaceId && workspaceDocument && (
        <PageContent
          title={currentTitle}
          titleFull={currentFullTitle}
          parent={parentBreadcrumbs}
          parentOverflowMenu={flatParentBreadcrumbs}
          onNavigateBack={handleViewerBack}
          rightSidebar={
            cameFromChat ? (
              <DocumentSidebar
                contextualDefault={DocumentTab.CHAT_REFERENCES}
                availableTabs={[
                  DocumentTab.DOCUMENT_METADATA,
                  DocumentTab.CHAT_REFERENCES,
                  DocumentTab.DOCUMENT_REFERENCES,
                ]}
                documentId={workspaceDocument?.blobName}
                selectionResetKey={workspaceDocument?.id}
                workspaceDocument={workspaceDocument ?? undefined}
                createReferenceTarget={{
                  documentId: workspaceDocument?.blobName,
                  source: DataSource.Workspace,
                  workspaceId,
                }}
              />
            ) : (
              <DocumentSidebar
                availableTabs={[
                  DocumentTab.DOCUMENT_METADATA,
                  DocumentTab.DOCUMENT_REFERENCES,
                  DocumentTab.VERSION_HISTORY,
                ]}
                documentId={workspaceDocument?.blobName}
                selectionResetKey={workspaceDocument?.id}
                workspaceDocument={workspaceDocument ?? undefined}
                createReferenceTarget={{
                  documentId: workspaceDocument?.blobName,
                  source: DataSource.Workspace,
                  workspaceId,
                }}
              />
            )
          }
        >
          <div className="p-4 py-1 xl:px-8 h-[calc(100vh-3rem)] flex flex-col gap-1">
            <div className="flex min-w-0 items-center gap-3">
              <DocumentPDFHeader
                className="min-w-0 flex-1"
                documentTitle={selectedVersion?.name}
                workspaceDocument={selectedVersion || null}
              />
              {/* PDFs and Office previews route here rather than through
                  WorkspaceDocumentPage, and pick revisions from the same
                  version-history sidebar, so they need the same readout. */}
              <WorkspaceDocumentVersionIndicator />
            </div>
            {previewSheets && previewSheets.length > 0 && (
              <div
                className="min-w-0 overflow-x-auto overflow-y-hidden border-b bg-muted/30 px-1 pt-1 pb-3"
                role="tablist"
                aria-label="Ark"
              >
                <div className="flex gap-1 w-max min-h-full">
                  {previewSheets.map((sheet) => (
                  <Button
                    key={sheet.index}
                    variant={selectedSheetIndex === sheet.index ? "secondary" : "ghost"}
                    size="sm"
                    className={cn(
                      "shrink-0",
                      selectedSheetIndex === sheet.index && "font-medium"
                    )}
                    onClick={() => setSelectedSheetIndex(sheet.index)}
                    role="tab"
                    aria-selected={selectedSheetIndex === sheet.index}
                    id={`sheet-tab-${sheet.index}`}
                  >
                    {sheet.name}
                  </Button>
                ))}
                </div>
              </div>
            )}
            {type &&
            !isWorkspaceDocumentPreviewableInViewer(
              type.toUpperCase() as WorkspaceDocumentType
            ) ? (
              <div className="h-full w-full flex justify-center items-center p-4">
                <Alert variant="destructive" className="max-w-md">
                  <AlertDescription className="space-y-3">
                    <p className="font-medium">Kunne ikke vise dokumentet</p>
                    <p className="text-sm text-muted-foreground">
                      {
                        "Dokumentet har en filtype, som ikke er understøttet. Du kan gennemgå dokumentet ved at downloade det."
                      }
                    </p>
                    <Button
                      onClick={() => download()}
                      variant="outline"
                      size="sm"
                      className="text-muted-foreground"
                      disabled={isDownloading}
                    >
                      {"Download"}
                      {isDownloading && <Spinner className="w-4"></Spinner>}
                    </Button>
                  </AlertDescription>
                </Alert>
              </div>
            ) : displayDocument ? (
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                <EmbedPDFViewer
                  key={`${blobName}-${selectedSheetIndex}-${
                    selectedVersion?.revisionVersion ||
                    workspaceDocument?.activeRevision ||
                    0
                  }`}
                  uri={downloadUrl}
                  document={displayDocument}
                  boundingBoxes={selectedBoundingBoxes}
                  documentCategory={DocumentCategory.Workspace}
                  isSpreadsheetPreview={Boolean(previewSheets?.length)}
                />
              </div>
            ) : null}
          </div>
        </PageContent>
      )}
    </>
  );
};

export default WorkspaceDocumentView;
